import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useQuery } from '@tanstack/react-query';
import { fetchSavegameBrowse } from '../../api/games';

interface Props {
  open:     boolean;
  gameId:   string | null;
  gameName: string;
  onClose:  () => void;
}

function fmtBytes(b: number): string {
  if (b >= 1_073_741_824) return `${(b / 1_073_741_824).toFixed(1)} GB`;
  if (b >= 1_048_576)     return `${(b / 1_048_576).toFixed(1)} MB`;
  if (b >= 1024)          return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

export function SavegameBrowseDialog({ open, gameId, gameName, onClose }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['savegame-browse', gameId],
    queryFn: () => fetchSavegameBrowse(gameId!),
    enabled: open && gameId !== null,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Savegame-Dateien — {gameName}</DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">Fehler beim Laden der Verzeichnisstruktur.</Alert>
        )}

        {data && data.entries.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Das Savegame-Verzeichnis ist leer.
          </Typography>
        )}

        {data && data.entries.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">Größe</TableCell>
                  <TableCell align="right">Geändert</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.entries.map((entry) => (
                  <TableRow key={entry.name} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {entry.type === 'directory'
                          ? <FolderIcon fontSize="small" color="action" />
                          : <InsertDriveFileIcon fontSize="small" color="action" />}
                        <Typography variant="body2" fontFamily="monospace">
                          {entry.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" color="text.secondary">
                        {entry.type === 'file' && entry.size_bytes !== null
                          ? fmtBytes(entry.size_bytes)
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(entry.modified_at)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {data && data.entries.length >= 500 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Anzeige auf 500 Einträge begrenzt.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}
